"use client";
import {
  AlertOctagon,
  ChevronRight,
  Database,
  Download,
  FileArchive,
  FileCode2,
  GitFork,
  ListFilter,
  LockKeyhole,
  Network,
  Radar,
  RefreshCw,
  ScanLine,
  Search,
  ShieldAlert,
  Terminal,
} from "lucide-react";
import {
  useState,
} from "react";
import {
  type DetectedAction,
  type ScanReport,
  type Severity,
} from "../../../lib/types";
import {
  severityOrder,
} from "../../shared/format";
import {
  StatusDot,
} from "../../shared/primitives";
import {
  AttackTechniqueChips,
  attackTechniqueSearchText,
} from "./AttackTechniqueChips";


export function ActionsView({ report }: { report: ScanReport }) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<
    DetectedAction["category"] | "all"
  >("all");
  const [severity, setSeverity] = useState<Severity | "all">("all");
  const categories = Array.from(
    new Set(report.actions.map((action) => action.category)),
  );
  const actions = report.actions.filter(
    (action) =>
      (category === "all" || action.category === category) &&
      (severity === "all" || action.severity === severity) &&
      (!query ||
        [
          action.title,
          action.description,
          action.entry,
          action.method ?? "",
          ...action.evidence,
          ...action.tags,
          ...(action.dataTypes ?? []),
          ...(action.destinations ?? []),
          attackTechniqueSearchText(action.techniques ?? []),
        ]
          .join(" ")
          .toLowerCase()
          .includes(query.toLowerCase())),
  );
  const actionCounts = {
    critical: report.actions.filter((action) => action.severity === "critical")
      .length,
    high: report.actions.filter((action) => action.severity === "high").length,
    reachable: report.actions.filter((action) => action.reachable).length,
    destinations: new Set(
      report.actions.flatMap((action) => action.destinations ?? []),
    ).size,
    techniques: new Set(
      report.actions.flatMap((action) =>
        (action.techniques ?? []).map((item) => item.id),
      ),
    ).size,
  };

  const actionIcon = (action: DetectedAction) => {
    if (action.category === "download") return <Download size={17} />;
    if (action.category === "execution" || action.category === "command")
      return <Terminal size={17} />;
    if (action.category === "network") return <Network size={17} />;
    if (action.category === "credential")
      return <LockKeyhole size={17} />;
    if (action.category === "filesystem") return <FileArchive size={17} />;
    if (action.category === "collection") return <Database size={17} />;
    if (action.category === "persistence") return <RefreshCw size={17} />;
    if (action.category === "evasion") return <ShieldAlert size={17} />;
    return <ScanLine size={17} />;
  };

  return (
    <div className="view-stack actions-workspace">
      {report.behaviorChains.length > 0 && (
        <section className="panel behavior-chain-panel">
          <div className="panel-heading">
            <div>
              <div className="section-kicker">CORRELATED BEHAVIOR</div>
              <h3>End-to-end behavior chains</h3>
            </div>
            <span>{report.behaviorChains.length} proven or correlated</span>
          </div>
          <div className="behavior-chain-list">
            {report.behaviorChains.map((chain) => (
              <article key={chain.id} className={`tone-${chain.severity}`}>
                <header>
                  <span>
                    <strong>{chain.title}</strong>
                    <small>
                      {chain.confidence} confidence · {chain.ordering} ·{" "}
                      {chain.reachable
                        ? "entry-point reachable"
                        : "reachability unproven"}
                    </small>
                  </span>
                  <span className={`severity-label tone-${chain.severity}`}>
                    {chain.severity}
                  </span>
                </header>
                <p>{chain.description}</p>
                <div className="chain-stage-row">
                  {chain.stages.map((stage) => (
                    <div key={`${chain.id}-${stage.index}`}>
                      <span>{stage.index + 1}</span>
                      <strong>{stage.title}</strong>
                      <small>{stage.detail}</small>
                      <code>{stage.method ?? stage.entry}</code>
                    </div>
                  ))}
                </div>
                {(chain.dataTypes.length > 0 ||
                  chain.destinations.length > 0) && (
                  <div className="chain-context">
                    {chain.dataTypes.map((item) => (
                      <code key={`data-${item}`}>{item}</code>
                    ))}
                    {chain.destinations.map((item) => (
                      <code key={`destination-${item}`}>{item}</code>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        </section>
      )}
      <section className="action-stat-grid">
        {[
          ["Critical chains", actionCounts.critical, AlertOctagon, "critical"],
          ["Entry reachable", actionCounts.reachable, GitFork, "high"],
          ["Resolved targets", actionCounts.destinations, Network, "medium"],
          ["ATT&CK techniques", actionCounts.techniques, Radar, "info"],
        ].map(([label, count, Icon, tone]) => {
          const ActionIcon = Icon as typeof ScanLine;
          return (
            <article key={String(label)} className={`tone-${tone}`}>
              <ActionIcon size={17} />
              <span>
                <strong>{Number(count).toLocaleString()}</strong>
                <small>{String(label)}</small>
              </span>
            </article>
          );
        })}
      </section>

      <section className="panel action-results-panel">
        <div className="table-toolbar">
          <div className="search-field">
            <Search size={15} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search actions, classes, methods, or evidence"
              aria-label="Search observed actions"
            />
          </div>
          <label className="select-control">
            <ListFilter size={14} />
            <select
              value={category}
              onChange={(event) =>
                setCategory(
                  event.target.value as DetectedAction["category"] | "all",
                )
              }
              aria-label="Action category"
            >
              <option value="all">All categories</option>
              {categories.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
          <label className="select-control">
            <ShieldAlert size={14} />
            <select
              value={severity}
              onChange={(event) =>
                setSeverity(event.target.value as Severity | "all")
              }
              aria-label="Action severity"
            >
              <option value="all">All severities</option>
              {severityOrder.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="action-result-count">
          {actions.length.toLocaleString()} of{" "}
          {report.actions.length.toLocaleString()} observed actions
        </div>
        <div className="action-card-list">
          {actions.map((action) => (
            <article className={`action-card tone-${action.severity}`} key={action.id}>
              <div className={`action-card-icon tone-${action.severity}`}>
                {actionIcon(action)}
              </div>
              <div className="action-card-body">
                <div className="action-card-title">
                  <span>
                    <strong>{action.title}</strong>
                    <small>
                      {action.category} · {action.confidence} confidence
                    </small>
                  </span>
                  <span className={`severity-label tone-${action.severity}`}>
                    {action.severity}
                  </span>
                </div>
                <p>{action.description}</p>
                <div className="action-proof-row">
                  <span
                    className={
                      action.reachable ? "is-reachable" : "is-unproven"
                    }
                  >
                    <StatusDot
                      severity={action.reachable ? "safe" : "medium"}
                    />
                    {action.reachable
                      ? "Entry-point reachable"
                      : "Reachability unproven"}
                  </span>
                  <span>
                    <GitFork size={11} />
                    {action.ordering === "proven"
                      ? "Ordered bytecode path"
                      : action.ordering === "partial"
                        ? "Partial path"
                        : "Correlated evidence"}
                  </span>
                  {action.entryPoint && <code>{action.entryPoint}</code>}
                </div>
                <div className="action-location">
                  <FileCode2 size={13} />
                  <code>{action.entry}</code>
                  {action.method && (
                    <>
                      <ChevronRight size={12} />
                      <code>{action.method}</code>
                    </>
                  )}
                </div>
                {action.evidence.length > 0 && (
                  <div className="action-evidence">
                    {action.evidence.map((item) => (
                      <code key={item}>{item}</code>
                    ))}
                  </div>
                )}
                {(action.dataTypes?.length ||
                  action.destinations?.length) && (
                  <div className="action-context-grid">
                    {Boolean(action.dataTypes?.length) && (
                      <div>
                        <span>DATA OBSERVED</span>
                        {action.dataTypes?.map((item) => (
                          <code key={item}>{item}</code>
                        ))}
                      </div>
                    )}
                    {Boolean(action.destinations?.length) && (
                      <div>
                        <span>RESOLVED TARGETS</span>
                        {action.destinations?.map((item) => (
                          <code key={item}>{item}</code>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {Boolean(action.techniques?.length) && (
                  <AttackTechniqueChips
                    techniques={action.techniques ?? []}
                  />
                )}
                <div className="action-tags">
                  {action.tags.map((tag) => (
                    <span key={tag}>{tag}</span>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </div>
        {!actions.length && (
          <div className="empty-panel">
            <ScanLine size={26} />
            <strong>No matching actions</strong>
            <span>Clear the filters or search for a different behavior.</span>
          </div>
        )}
      </section>
    </div>
  );
}
